import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import {
  Grid,
  Typography,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { setDecimalPrecision } from 'utils/helpers'
import { ROUNDING_PRECISIONS } from 'utils/constants'
import { aggregateTicketMaterials } from 'utils/aggregateTicketMaterials'
import { roundTo } from 'utils/data-manipulation/valueFormatter'

const NumberTableCell = styled(TableCell)(() => ({
  paddingLeft: '8px',
  paddingRight: '8px',
}))

const SummaryRows = ({
  tickets,
  taxRate,
  priceEntriesByPurchaseOrderId,
  priceSheet,
  defaultPriceSheet,
  defaultPriceType,
  showDeductions,
  showWeightDecimals,
  pricePrepend = '$',
  appliedRoundingDelta,
}) => {
  const { settings, defaultYardUm } = useSelector(st => st.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const { materials, totalData } = aggregateTicketMaterials({
    tickets,
    priceSheet,
    priceEntriesByPurchaseOrderId,
    isSummary: true,
    defaultPriceSheet,
    defaultPriceType,
    pricePrepend,
    disableUmDropdowns,
    defaultYardUm,
  })
  const { subtotal, totalChargeback, totalTruck } = tickets.reduce(
    (aggregate, ticket) => ({
      subtotal: aggregate.subtotal + (ticket.subTotal || 0),
      totalChargeback: aggregate.totalChargeback + (ticket.freightCost?.chargeBack || 0),
      totalTruck: {
        gross: aggregate.totalTruck.gross + (ticket.truckWeight?.gross?.amount || 0),
        tare: aggregate.totalTruck.tare + (ticket.truckWeight?.tare?.amount || 0),
        net:
          aggregate.totalTruck.net +
          ((ticket.truckWeight?.gross?.amount || 0) - (ticket.truckWeight?.tare?.amount || 0)),
      },
    }),
    { subtotal: 0, totalChargeback: 0, totalTruck: { gross: 0, tare: 0, net: 0 } },
  )

  const roundedSubtotal = roundTo(subtotal + appliedRoundingDelta, ROUNDING_PRECISIONS.COST)
  const printTaxValue = roundTo((roundedSubtotal * taxRate) / 100, ROUNDING_PRECISIONS.COST)
  const printTotalValue = roundedSubtotal + printTaxValue

  return (
    <>
      {materials.map(material => (
        <TableRow key={`${material.id}-summary`}>
          <TableCell>{material.materialName}</TableCell>
          <NumberTableCell align="right">{material.gross}</NumberTableCell>
          <NumberTableCell align="right">{material.tare}</NumberTableCell>
          {showDeductions && <NumberTableCell align="right">{material.deductions}</NumberTableCell>}
          <NumberTableCell align="right">{material.net}</NumberTableCell>
          <NumberTableCell align="right">{material.price}</NumberTableCell>
          <NumberTableCell align="right">{material.total}</NumberTableCell>
        </TableRow>
      ))}
      {totalChargeback ? (
        <TableRow key="chargeback-summary">
          <TableCell>Freight Charge</TableCell>
          <NumberTableCell align="right" />
          <NumberTableCell align="right" />
          {showDeductions && <NumberTableCell align="right" />}
          <NumberTableCell align="right" />
          <NumberTableCell align="right" />
          <NumberTableCell align="right">
            {`(${pricePrepend}${setDecimalPrecision(totalChargeback, { decimalPrecisionOverride: 2 })})`}
          </NumberTableCell>
        </TableRow>
      ) : null}
      {printTaxValue ? (
        <>
          <TableRow key="subtotal-summary">
            <TableCell>Subtotal</TableCell>
            <NumberTableCell align="right">
              {setDecimalPrecision(totalData.gross, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })}
            </NumberTableCell>
            <NumberTableCell align="right">
              {setDecimalPrecision(totalData.tare, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })}
            </NumberTableCell>
            {showDeductions && <NumberTableCell align="right" />}
            <NumberTableCell align="right">
              {setDecimalPrecision(totalData.net, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })}
            </NumberTableCell>
            <NumberTableCell align="right" />
            <NumberTableCell align="right">
              {pricePrepend}
              {setDecimalPrecision(roundedSubtotal, { decimalPrecisionOverride: 2 })}
            </NumberTableCell>
          </TableRow>
          <TableRow key="tax-summary">
            <TableCell>Tax</TableCell>
            <NumberTableCell align="right" />
            <NumberTableCell align="right" />
            {showDeductions && <NumberTableCell align="right" />}
            <NumberTableCell align="right" />
            <NumberTableCell align="right" />
            <NumberTableCell align="right">
              {pricePrepend}
              {setDecimalPrecision(printTaxValue, { decimalPrecisionOverride: 2 })}
            </NumberTableCell>
          </TableRow>
        </>
      ) : null}
      <TableRow key="total-summary" sx={{ backgroundColor: 'lightgray' }}>
        <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.gross, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.tare, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        {showDeductions && <NumberTableCell sx={{ fontWeight: 600 }} align="right" />}
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.net, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell align="right" />
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {pricePrepend}
          {setDecimalPrecision(defaultPriceType === 'buyPrice' ? printTotalValue : totalData.total, {
            decimalPrecisionOverride: 2,
          })}
        </NumberTableCell>
      </TableRow>
      <TableRow key="truck-total">
        <TableCell>Truck Total</TableCell>
        <NumberTableCell align="right">
          {setDecimalPrecision(totalTruck.gross, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell align="right">
          {setDecimalPrecision(totalTruck.tare, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell />
        <NumberTableCell align="right">
          {setDecimalPrecision(totalTruck.net, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell />
        <NumberTableCell />
      </TableRow>
      <TableRow sx={{ height: '32px' }} />
    </>
  )
}

const PrintTicketsSummary = ({
  title,
  headers,
  tickets,
  taxRate,
  priceEntriesByPurchaseOrderId,
  priceSheet,
  defaultPriceSheet,
  defaultPriceType,
  showDeductions,
  showWeightDecimals,
  pricePrepend,
  appliedRoundingDelta,
}) => (
  <Grid>
    <Stack direction="row" mb={1}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
    </Stack>

    <TableContainer sx={{ overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers
              .filter(header => (!showDeductions ? header !== 'Deductions' : true))
              .map((header, index) =>
                index > 0 ? (
                  <NumberTableCell sx={{ fontWeight: 600 }} align="right" key={header}>
                    {header}
                  </NumberTableCell>
                ) : (
                  <TableCell sx={{ fontWeight: 600 }} align="left" key={header}>
                    {header}
                  </TableCell>
                ),
              )}
          </TableRow>
        </TableHead>
        <TableBody>
          <SummaryRows
            tickets={tickets}
            taxRate={taxRate}
            priceEntriesByPurchaseOrderId={priceEntriesByPurchaseOrderId}
            priceSheet={priceSheet}
            defaultPriceSheet={defaultPriceSheet}
            defaultPriceType={defaultPriceType}
            showDeductions={showDeductions}
            showWeightDecimals={showWeightDecimals}
            pricePrepend={pricePrepend}
            appliedRoundingDelta={appliedRoundingDelta}
          />
        </TableBody>
      </Table>
    </TableContainer>
  </Grid>
)

PrintTicketsSummary.propTypes = {
  title: PropTypes.string,
  headers: PropTypes.array,
  tickets: PropTypes.array,
  taxRate: PropTypes.number,
  priceEntriesByPurchaseOrderId: PropTypes.object,
  priceSheet: PropTypes.object,
  defaultPriceSheet: PropTypes.object,
  defaultPriceType: PropTypes.string,
  showDeductions: PropTypes.bool,
  showWeightDecimals: PropTypes.bool,
  pricePrepend: PropTypes.string,
  appliedRoundingDelta: PropTypes.number,
}

PrintTicketsSummary.defaultProps = {
  title: '',
  headers: [],
  tickets: [],
  taxRate: 0,
  priceEntriesByPurchaseOrderId: {},
  priceSheet: {},
  defaultPriceSheet: {},
  defaultPriceType: 'buyPrice',
  showDeductions: false,
  showWeightDecimals: false,
  pricePrepend: '$',
  appliedRoundingDelta: 0,
}

export default PrintTicketsSummary
